import { ARProvider } from "@animech/ar";
import {
	ModalRoot,
	ModalStack,
	ModalStackChangeCallback,
	StyleProvider,
	Theme,
	TooltipRoot,
} from "@animech/ui";
import {
	FunctionComponent,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from "react";
import {
	OutOfStockWarningElementContainer,
	SceneElementContainer,
} from "../containers";
import { ARRootWrapper } from "./ar-root-wrapper";
import * as Styled from "./app.style";
import {
	useUiData,
	GlobalStyle,
	LayoutRootPanel,
	LoadingPanel,
	DeveloperPanel,
	QueryErrorPanel,
	ApiNotifications,
} from "src/ui";
import { BuildInfo } from "src/utility";
import { ApiDataContext, useSave } from "src/api";

export const screenStackType = "screen" as const;

export type AppProps = {
	theme: Theme;
};

export const App: FunctionComponent<AppProps> = ({ theme }) => {
	const { modals, notifications, arConfig, getUrl } = useUiData();
	const saveConfiguration = useSave();

	const generatePageUrl = useCallback(async () => {
		await saveConfiguration();
		return getUrl();
	}, [saveConfiguration, getUrl]);

	const modalStack = useMemo(() => new ModalStack(), []);
	const { apiNotification } = useContext(ApiDataContext);
	const [hasScreenModals, setHasScreenModals] = useState(false);
	useEffect(() => {
		const callback: ModalStackChangeCallback = (modals) =>
			setHasScreenModals(
				modals.filter((modal) => modal.type === screenStackType).length > 0,
			);
		modalStack.onChange(callback);
		return () => modalStack.offChange(callback);
	}, [modalStack]);

	// We will block the app from running if the API is unable to perform it's initial request.
	const errorIsCritical =
		apiNotification?.status === "error" &&
		apiNotification.requestType === "init" &&
		apiNotification.name !== "ZodError";
	if (errorIsCritical) {
		return (
			<CriticalErrorWrapper
				theme={theme}
				error={`API Error: ${apiNotification.message}`}
			/>
		);
	}

	const content = (
		<StyleProvider
			theme={theme}
			modalStack={modalStack}
			modalManager={modals}
			notificationManager={notifications}
		>
			<GlobalStyle />
			<Styled.Root>
				{BuildInfo.mode !== "production" && (
					<Styled.Developer>
						<DeveloperPanel />
					</Styled.Developer>
				)}
				<Styled.Main data-screen-modals={hasScreenModals || undefined}>
					<LayoutRootPanel />
				</Styled.Main>
				<ModalRoot />
				<TooltipRoot />
				<SceneElementContainer />
				<OutOfStockWarningElementContainer />
				<ApiNotifications />
			</Styled.Root>
		</StyleProvider>
	);

	if (arConfig) {
		return (
			<ARProvider {...arConfig} generatePageUrl={generatePageUrl}>
				{content}
				<ARRootWrapper />
			</ARProvider>
		);
	}

	return content;
};

export const BootstrappingApp: FunctionComponent<AppProps> = ({ theme }) => (
	<StyleProvider theme={theme}>
		<GlobalStyle />
		<LoadingPanel />
	</StyleProvider>
);

export const CriticalErrorWrapper: FunctionComponent<
	AppProps & { error: string }
> = ({ theme, error }) => (
	<StyleProvider theme={theme}>
		<GlobalStyle />
		<QueryErrorPanel error={error} />
	</StyleProvider>
);
